/**
 * The commit screens that present the whole flow on ONE page, with no per-section
 * Next / Apply / Confirm gates. Milestone sections rendered under one of these lift their
 * percentage validity and autosave state to the parent, which gates its single commit
 * button on them, and render no Confirm button of their own.
 *
 * 'direct-commit'    — Direct Commitment (expenditure)
 * 'ticketing-commit' — Commit Ticketing
 * 'revenue-commit'   — Commit Revenue
 * 'sponsor-commit'   — Commit Sponsor
 */
export const SINGLE_SCREEN_PARENT_SECTIONS = ['direct-commit', 'ticketing-commit', 'revenue-commit', 'sponsor-commit'];

export const isSingleScreenCommitSection = (parentSection) => SINGLE_SCREEN_PARENT_SECTIONS.includes(parentSection);

export const calculateUncommittedExpendituresTotal = (uncommittedExpenditures) => {
    return uncommittedExpenditures.reduce(
        (acc, item) =>
            acc +
            (item.expense?.type === 'project'
                ? getProjectUncommittedTotal(item)
                : item.originalCashflowObject.uncommitted || item.detailed_amount.original_total),
        0, // initial value
    );
};

export const getProjectUncommittedTotal = (item) => {
    return item.uncommitted_expenditures.reduce(
        (acc, item) => acc + (item.originalCashflowObject.uncommitted || item.detailed_amount.original_total),
        0, // initial value
    );
};

/**
 * The SINGLE committing-totals computation for the Direct Commitment screen.
 *
 * Given the loaded `lineItems` tree (top-level UncommittedExpenditure rows, each with a
 * `cashflow_object` committing split and, for project containers, a `uncommitted_expenditures`
 * array of direct member rows), returns `{ cash, vik }` = the sum of the committing amount of
 * every committable LEAF line, counting each leaf exactly once.
 *
 * Container rows (project / template / pack parents) contribute ONLY via their committable
 * children, never via their own aggregate:
 *   - A PROJECT row is a pure container: its own `cashflow_object` (and the project expense's
 *     display cashflowObject, e.g. 20,000) is informational and must NOT be summed. Its direct
 *     members (SBL / template / pack) ARE the committing leaves — each carries its own committing
 *     `cashflow_object` split (a template's split already aggregates its template_children, a
 *     pack's split already aggregates its pack contents), so we sum the members directly and do
 *     NOT descend further.
 *   - A standalone top-level template / pack / SBL row carries its own committing split directly.
 *
 * This is the source of truth shared by the impact headline, the commitment table footer, and
 * the milestone Cash / VIK value base — so all three always agree.
 *
 * @param {Array} lineItems
 * @returns {{cash: number, vik: number}}
 */
const sumLeafTotals = (lineItems, splitFor) =>
    (lineItems || []).reduce(
        (totals, item) => {
            // Project containers never contribute their own aggregate — only their members.
            if (item?.expense?.type === 'project') {
                const members = Array.isArray(item.uncommitted_expenditures) ? item.uncommitted_expenditures : [];
                return members.reduce((acc, member) => {
                    const { cash, vik } = splitFor(member);
                    return { cash: acc.cash + cash, vik: acc.vik + vik };
                }, totals);
            }

            const { cash, vik } = splitFor(item);
            return { cash: totals.cash + cash, vik: totals.vik + vik };
        },
        { cash: 0, vik: 0 },
    );

export const committingTotals = (lineItems = []) =>
    sumLeafTotals(lineItems, (row) => {
        const cf = row?.cashflow_object ?? {};
        return { cash: Number(cf.cash) || 0, vik: Number(cf.vik) || 0 };
    });

/**
 * The matching "Uncommitted now" totals for the impact headline — the pre-commit UNCOMMITTED
 * balance of every committable LEAF, split cash / VIK, using the IDENTICAL leaf traversal as
 * committingTotals (so the headline's now / committing / after never diverge and a default full
 * commit nets to 0 after).
 *
 * IMPORTANT: `cash` / `vik` on a leaf's cashflow object are its FULL totals — for a line that is
 * already partially committed they include the already-committed portion. The Direct Commitment
 * screen must never surface an already-committed amount, so we take the leaf's `uncommitted`
 * balance (combined) and split it across cash / VIK in the same proportion as the total. A
 * fully-uncommitted leaf (uncommitted === total) yields its cash / vik totals unchanged.
 *
 * @param {Array} lineItems
 * @returns {{cash: number, vik: number}}
 */
export const uncommittedNowTotals = (lineItems = []) =>
    sumLeafTotals(lineItems, (row) => {
        const cf = row?.originalCashflowObject ?? row?.cashflow_object ?? {};
        const cashTotal = Number(cf.cash) || 0;
        const vikTotal = Number(cf.vik) || 0;

        // Prefer the explicit uncommitted balance; fall back to total − committed, then to the
        // raw totals when neither is present (older payloads without commit tracking).
        const uncommitted = cf.uncommitted ?? (cf.total !== undefined ? (Number(cf.total) || 0) - (Number(cf.committed) || 0) : undefined);
        if (uncommitted === undefined || uncommitted === null) {
            return { cash: cashTotal, vik: vikTotal };
        }

        const uncommittedValue = Number(uncommitted) || 0;
        const grossTotal = cashTotal + vikTotal;
        if (grossTotal <= 0) {
            return { cash: uncommittedValue, vik: 0 };
        }

        return {
            cash: uncommittedValue * (cashTotal / grossTotal),
            vik: uncommittedValue * (vikTotal / grossTotal),
        };
    });

/**
 * A milestone's value against its section total. The value is a DERIVED quantity — the section
 * total times the milestone's share — so it is computed here rather than trusted from wherever it
 * was last stored: a stored figure written by an effect always lags its inputs by one commit,
 * which is how a milestone could read the PREVIOUS committing amount while its own percentage
 * read 100%.
 *
 * A stored figure that already agrees with the product to within half a cent is returned
 * untouched. That matters because the Value cell is editable: a typed value has its percentage
 * back-computed from it, so re-deriving would round-trip through the division and could shed a
 * cent from the figure the user just typed.
 *
 * @param {number|string|null} storedValue  the value currently held in form state, if any
 * @param {number} totalValue               the section total the percentages allocate
 * @param {number} percentage               the milestone's share, 0-100
 * @returns {number}
 */
export const reconcileMilestoneValue = (storedValue, totalValue, percentage) => {
    const stored = storedValue === null || storedValue === undefined || storedValue === '' ? null : Number(storedValue);
    const total = Number(totalValue);
    const perc = Number(percentage);

    if (!isFinite(total) || total <= 0 || !isFinite(perc)) {
        return stored !== null && isFinite(stored) ? stored : 0;
    }

    const derived = (total * perc) / 100;

    return stored !== null && isFinite(stored) && Math.abs(stored - derived) < 0.005 ? stored : derived;
};
